import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { ShoppingBag, Menu, X, User, LogOut, Search, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import WelcomePopup from "./WelcomePopup";

const MaintenanceScreen = ({ settings }) => (
    <div data-testid="maintenance-screen" className="min-h-screen bg-charcoal-900 text-[#1A1A1A] flex items-center justify-center px-6">
        <div className="text-center max-w-lg animate-fade-up">
            <div className="w-14 h-14 border border-gold/60 flex items-center justify-center mx-auto mb-8"><span className="font-display text-gold text-2xl">M</span></div>
            {settings?.business_name && <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-4">{settings.business_name}</div>}
            <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-5">We'll be right back.</h1>
            <p className="text-[#6B645C] leading-relaxed">Our showroom is undergoing a brief update. Please check back shortly{settings?.business_phone ? <> or call <span className="text-gold">{settings.business_phone}</span></> : ""}.</p>
        </div>
    </div>
);

const NavItem = ({ to, children, onClick }) => (
    <NavLink
        to={to}
        onClick={onClick}
        data-testid={`nav-${to.replace(/\//g, "") || "home"}`}
        className={({ isActive }) =>
            `text-[13px] tracking-[0.18em] uppercase transition-colors duration-300 ${isActive ? "text-gold" : "text-[#33302C] hover:text-gold"}`
        }
    >
        {children}
    </NavLink>
);

export default function Layout({ children }) {
    const { user, logout, isAdmin } = useAuth();
    const { count } = useCart();
    const [open, setOpen] = useState(false);
    const [settings, setSettings] = useState(null);
    const nav = useNavigate();
    const location = useLocation();
    useEffect(() => { api.get("/site/settings").then(r => setSettings(r.data)).catch(() => {}); }, []);

    const allowDuringMaintenance = ["/login", "/register", "/admin"].some(p => location.pathname.startsWith(p));
    if (settings?.maintenance_mode && !isAdmin && !allowDuringMaintenance) {
        return <MaintenanceScreen settings={settings} />;
    }
    const socials = [
        { url: settings?.facebook_url, Icon: Facebook, label: "Facebook" }, { url: settings?.instagram_url, Icon: Instagram, label: "Instagram" },
        { url: settings?.twitter_url, Icon: Twitter, label: "X (Twitter)" }, { url: settings?.youtube_url, Icon: Youtube, label: "YouTube" },
    ].filter(s => s.url);

    return (
        <div className="min-h-screen bg-charcoal-900 text-[#1A1A1A] flex flex-col">
            <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:bg-gold focus:text-white focus:px-4 focus:py-2 text-xs uppercase tracking-widest">Skip to content</a>
            <header className="fixed top-0 inset-x-0 z-50 bg-[#F7F3EB]/92 backdrop-blur-2xl border-b border-[#E2D4C1]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                    <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
                        {settings?.logo_url ? (
                            <img src={settings.logo_url} alt={settings?.business_name || "logo"} data-testid="site-logo" className="w-9 h-9 object-contain shrink-0" />
                        ) : (
                            <div className="w-9 h-9 border border-gold/60 flex items-center justify-center shrink-0">
                                <span className="font-display text-gold text-lg">M</span>
                            </div>
                        )}
                        <div className="leading-tight">
                            <div data-testid="site-business-name" className="font-display text-lg md:text-xl tracking-wide leading-none">{settings?.business_name || "MBG Auto Sales"}</div>
                            <div className="hidden sm:block text-[8px] md:text-[9px] tracking-[0.22em] text-[#6B645C] uppercase mt-1">Luxury Auto • Window Film • Accessories</div>
                        </div>
                    </Link>

                    <nav aria-label="Main" className="hidden lg:flex items-center gap-9">
                        <NavItem to="/">Home</NavItem>
                        <NavItem to="/cars">Vehicles</NavItem>
                        <NavItem to="/automotive-window-film">Window Film</NavItem>
                        <NavItem to="/tools">Tools &amp; Accessories</NavItem>
                        <NavItem to="/about">About</NavItem>
                        <NavItem to="/contact">Contact</NavItem>
                    </nav>

                    <div className="flex items-center gap-3">
                        <Link to="/cart" data-testid="cart-icon" aria-label={count > 0 ? `Cart, ${count} item${count === 1 ? "" : "s"}` : "Cart"} className="relative p-2 text-[#33302C] hover:text-gold transition-colors">
                            <ShoppingBag size={20} aria-hidden="true" />
                            {count > 0 && (
                                <span data-testid="cart-count" aria-hidden="true" className="absolute -top-1 -right-1 bg-gold text-white text-[10px] font-semibold w-5 h-5 flex items-center justify-center rounded-full">{count}</span>
                            )}
                        </Link>
                        {user ? (
                            <div className="hidden md:flex items-center gap-2">
                                {isAdmin && (
                                    <Button data-testid="admin-link" onClick={() => nav("/admin")} variant="outline" className="border-gold/40 text-gold hover:bg-gold hover:text-white rounded-none uppercase tracking-widest text-xs">Admin</Button>
                                )}
                                <button data-testid="account-link" aria-label="My account" title="My account" onClick={() => nav("/account")} className="text-[#33302C] hover:text-gold p-2"><User size={18} aria-hidden="true" /></button>
                                <button data-testid="logout-btn" aria-label="Sign out" title="Sign out" onClick={() => { logout(); nav("/"); }} className="text-[#33302C] hover:text-gold p-2"><LogOut size={18} aria-hidden="true" /></button>
                            </div>
                        ) : (
                            <Button data-testid="login-btn" onClick={() => nav("/login")} className="hidden md:inline-flex bg-gold hover:bg-gold-hover text-white rounded-none uppercase tracking-widest text-xs font-semibold px-6">Sign In</Button>
                        )}
                        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="mobile-menu" className="lg:hidden text-[#1A1A1A] p-2">
                            {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
                        </button>
                    </div>
                </div>
                {open && (
                    <nav id="mobile-menu" aria-label="Main" className="lg:hidden border-t border-[#E2D4C1] bg-[#F7F3EB]/98 px-6 py-6 space-y-4">
                        <NavItem to="/" onClick={() => setOpen(false)}>Home</NavItem><br/>
                        <NavItem to="/cars" onClick={() => setOpen(false)}>Vehicles</NavItem><br/>
                        <NavItem to="/automotive-window-film" onClick={() => setOpen(false)}>Window Film</NavItem><br/>
                        <NavItem to="/tools" onClick={() => setOpen(false)}>Tools &amp; Accessories</NavItem><br/>
                        <NavItem to="/about" onClick={() => setOpen(false)}>About</NavItem><br/>
                        <NavItem to="/contact" onClick={() => setOpen(false)}>Contact</NavItem><br/>
                        {!user && <NavItem to="/login" onClick={() => setOpen(false)}>Sign In</NavItem>}
                        {user && <NavItem to="/account" onClick={() => setOpen(false)}>Account</NavItem>}
                        {isAdmin && <><br/><NavItem to="/admin" onClick={() => setOpen(false)}>Admin</NavItem></>}
                        {user && <><br/><button type="button" onClick={() => { setOpen(false); logout(); nav("/"); }} className="text-[13px] tracking-[0.18em] uppercase text-[#33302C] hover:text-gold">Sign Out</button></>}
                    </nav>
                )}
            </header>

            <main id="main-content" tabIndex={-1} className="flex-1 pt-20 focus:outline-none">{children}</main>

            <footer className="border-t border-[#E2D4C1] bg-[#E2D4C1] mt-24">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-4 gap-10">
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-3 mb-4">
                            {settings?.logo_url ? (
                                <img src={settings.logo_url} alt={settings?.business_name || "logo"} className="w-9 h-9 object-contain" />
                            ) : (
                                <div className="w-9 h-9 border border-gold/60 flex items-center justify-center"><span className="font-display text-gold text-lg">M</span></div>
                            )}
                            <div data-testid="footer-business-name" className="font-display text-xl">{settings?.business_name || "MBG Auto Sales"}</div>
                        </div>
                        {settings?.footer_description && <p data-testid="footer-description" className="text-[#6B645C] max-w-md text-sm leading-relaxed">{settings.footer_description}</p>}
                        {(socials.length > 0 || settings?.business_phone || settings?.business_email) && (
                            <div className="mt-5 space-y-2">
                                {settings?.business_phone && <div data-testid="footer-phone" className="text-sm text-[#33302C]">{settings.business_phone}</div>}
                                {settings?.business_email && <div data-testid="footer-email" className="text-sm text-[#33302C]">{settings.business_email}</div>}
                                {socials.length > 0 && (
                                    <div className="flex gap-3 pt-2" data-testid="footer-socials">
                                        {socials.map(({ url, Icon, label }, i) => (
                                            <a key={i} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} className="w-9 h-9 border border-[#E2D4C1] grid place-items-center text-[#33302C] hover:text-gold hover:border-gold/40 transition-colors"><Icon size={15} aria-hidden="true" /></a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-gold mb-4">Showroom</div>
                        <ul className="space-y-2 text-sm text-[#33302C]">
                            <li><Link to="/cars" className="hover:text-gold">Vehicles</Link></li>
                            <li><Link to="/automotive-window-film" className="hover:text-gold">Window Film</Link></li>
                            <li><Link to="/tools" className="hover:text-gold">Tools &amp; Accessories</Link></li>
                        </ul>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-gold mb-4">Company</div>
                        <ul className="space-y-2 text-sm text-[#33302C]">
                            <li><Link to="/about" className="hover:text-gold">About</Link></li>
                            <li><Link to="/contact" className="hover:text-gold">Contact</Link></li>
                            <li><Link to="/account" className="hover:text-gold">Account</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-[#E2D4C1] py-6 text-center text-[11px] tracking-[0.2em] uppercase text-[#6B645C]" data-testid="footer-copyright">
                    {settings?.copyright_text || (settings?.business_name ? `© ${new Date().getFullYear()} ${settings.business_name} — All rights reserved` : "")}
                </div>
            </footer>
            <WelcomePopup />
        </div>
    );
}
